import { createAsyncThunk } from '@reduxjs/toolkit';
import { AdminRepository } from 'repositories/AdminRepository';
import { IAdminPaymentState } from 'store/slices/adminPaymentSlice';
import { IAuthState } from 'store/slices/authSlice';
import { IAdminPayment, TAdminPaymentHistoryQuery, TPaymentsCalculateQuery } from 'types';
import { EApiResponseStatus, IApiResponseDetails } from 'types/api';
import { getFormattedServerDateTimeRequest } from 'utils';

export const fetchPaymentHistory = createAsyncThunk<
  { content: IAdminPayment[]; details: IApiResponseDetails },
  void,
  { state: { auth: IAuthState; adminPayments: IAdminPaymentState } }
>('adminPayments/fetchPaymentHistory', async (_, { getState }) => {
  const {
    auth: { token },
    adminPayments: {
      currentPage,
      itemsPerPage,
      sortBy,
      sortDir,
      search,
      confirmation,
      createdFromDate,
      createdToDate,
      updatedFromDate,
      updatedToDate,
    },
  } = getState();
  if (!token) throw Error('no token');

  const params: TAdminPaymentHistoryQuery = {
    page: String(currentPage),
    size: String(itemsPerPage),
  };
  if (sortBy) {
    params.sort = sortDir ? `payment.${sortBy},${sortDir.toLowerCase()}` : sortBy;
  }
  if (search) params.filter = search;
  if (createdFromDate) params.createdfrom = createdFromDate;
  if (createdToDate) params.createdto = createdToDate;
  if (updatedFromDate) params.updatedfrom = updatedFromDate;
  if (updatedToDate) params.updatedto = updatedToDate;
  if (confirmation) params.status = confirmation;

  const adminPaymentHistoryResponse = await AdminRepository.getPaymentHistory(token, params);

  if (adminPaymentHistoryResponse.status === EApiResponseStatus.FAILURE) {
    throw Error(adminPaymentHistoryResponse.error);
  }

  return adminPaymentHistoryResponse.data;
});

export const fetchExportPaymentHistory = createAsyncThunk<
  { exportData: string },
  void,
  { state: { auth: IAuthState } }
>('adminPayments/fetchExportPaymentHistory', async (_, { getState }) => {
  const {
    auth: { token },
  } = getState();

  if (!token) throw Error('no token');

  const adminExportPaymentHistoryResponse = await AdminRepository.getPaymentHistoryExport(token);

  if (adminExportPaymentHistoryResponse.status === EApiResponseStatus.FAILURE) {
    throw Error(adminExportPaymentHistoryResponse.error);
  }

  return {
    exportData: adminExportPaymentHistoryResponse.data,
  };
});

export const fetchPaymentsCalculate = createAsyncThunk<
  object,
  void,
  { state: { auth: IAuthState; adminPayments: IAdminPaymentState } }
>('adminPayments/fetchPaymentsCalculate', async (_, { getState }) => {
  const {
    auth: { token },
    adminPayments: { serverDateTime },
  } = getState();
  if (!token) throw Error('no token');
  if (!serverDateTime) throw Error('no server date');

  const params: TPaymentsCalculateQuery = {
    date: getFormattedServerDateTimeRequest(serverDateTime),
  };
  const paymentsCalculateResponse = await AdminRepository.getPaymentsCalculate(token, params);

  if (paymentsCalculateResponse.status === EApiResponseStatus.FAILURE) {
    throw Error(paymentsCalculateResponse.error);
  }

  return paymentsCalculateResponse.data;
});

export const fetchServerDateTime = createAsyncThunk<
  { time: string },
  void,
  { state: { auth: IAuthState } }
>('adminPayments/fetchPaymentsCalculate', async (_, { getState }) => {
  const {
    auth: { token },
  } = getState();
  if (!token) throw Error('no token');

  const serverDateTimeResponse = await AdminRepository.getServerDateTime(token);

  if (serverDateTimeResponse.status === EApiResponseStatus.FAILURE) {
    throw Error(serverDateTimeResponse.error);
  }

  return serverDateTimeResponse.data;
});
